/**
 * Modal dialog for adding Outcome/Opportunity/Solution/Story/Experiment nodes.
 */
import { useState } from 'react';
import { Modal, Button, FormRow, TwoCol } from '../ui';

const MOSCOW_OPTS = ['Must have', 'Should have', 'Could have', "Won't have (MVP)"];

type AddNodeModalProps = {
    type: 'outcome' | 'opportunity' | 'solution' | 'story' | 'experiment' | 'enablerNode';
    onConfirm: (data: { title: string; description?: string; moscow?: string; asA?: string; iWant?: string; soThat?: string; acceptanceCriteria?: string; assumption?: string; method?: string; successMetric?: string; validated?: string; }) => void;
    onClose: () => void;
};

export default function AddNodeModal({ type, onConfirm, onClose }: AddNodeModalProps) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [moscow, setMoscow] = useState('Could have');
    const [asA, setAsA] = useState('');
    const [iWant, setIWant] = useState('');
    const [soThat, setSoThat] = useState('');
    const [ac, setAc] = useState('');
    const [assumption, setAssumption] = useState('');
    const [method, setMethod] = useState('');
    const [successMetric, setSuccessMetric] = useState('');

    /**
     * Submit form data as a new node payload.
     */
    const submit = () => {
        if (!title.trim()) return;
        const payload = {
            title,
            description,
            moscow,
            asA,
            iWant,
            soThat,
            acceptanceCriteria: ac,
            assumption,
            method,
            successMetric,
        };
        onConfirm(payload);
    };

    return (
        <Modal title={type} onClose={onClose} headerVariant={type} closeOnOverlayClick={true}>
            <FormRow>
                <label className='form-label'>Title *</label>
                <input
                    className='form-input'
                    autoFocus
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submit()}
                    placeholder={placeholder(type)}
                />
            </FormRow>

            {type !== 'story' && type !== 'enablerNode' && (
                <FormRow>
                    <label className='form-label'>Description</label>
                    <textarea
                        className='form-textarea form-textarea-h64'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </FormRow>
            )}

            {(type === 'solution' || type === 'story' || type === 'enablerNode') && (
                <FormRow>
                    <label className='form-label'>MoSCoW</label>
                    <select className='form-select' value={moscow} onChange={(e) => setMoscow(e.target.value)}>
                        {MOSCOW_OPTS.map((o) => (
                            <option key={o}>{o}</option>
                        ))}
                    </select>
                </FormRow>
            )}

            {(type === 'story' || type === 'enablerNode') && (
                <>
                    <TwoCol>
                        <FormRow>
                            <label className='form-label'>As a...</label>
                            <input
                                className='form-input'
                                value={asA}
                                onChange={(e) => setAsA(e.target.value)}
                                placeholder='early-stage hardware engineer'
                            />
                        </FormRow>
                        <FormRow>
                            <label className='form-label'>I want...</label>
                            <input
                                className='form-input'
                                value={iWant}
                                onChange={(e) => setIWant(e.target.value)}
                            />
                        </FormRow>
                    </TwoCol>
                    <FormRow>
                        <label className='form-label'>So that...</label>
                        <input className='form-input' value={soThat} onChange={(e) => setSoThat(e.target.value)} />
                    </FormRow>
                    <FormRow>
                        <label className='form-label'>Acceptance criteria (one per line)</label>
                        <textarea
                            className='form-textarea form-textarea-h80'
                            value={ac}
                            onChange={(e) => setAc(e.target.value)}
                            placeholder={'Result appears within 10s\\nShows pass/fail indicator'}
                        />
                    </FormRow>
                </>
            )}

            {type === 'experiment' && (
                <>
                    <FormRow>
                        <label className='form-label'>Assumption *</label>
                        <input
                            className='form-input'
                            value={assumption}
                            onChange={(e) => setAssumption(e.target.value)}
                            placeholder='We believe ...'
                        />
                    </FormRow>
                    <FormRow>
                        <label className='form-label'>Method *</label>
                        <input
                            className='form-input'
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            placeholder='Test with ...'
                        />
                    </FormRow>
                    <FormRow>
                        <label className='form-label'>Success metric *</label>
                        <input
                            className='form-input'
                            value={successMetric}
                            onChange={(e) => setSuccessMetric(e.target.value)}
                            placeholder='Measure ...'
                        />
                    </FormRow>
                </>
            )}

            <div className='modal-actions'>
                <Button variant='secondary' onClick={onClose}>
                    Cancel
                </Button>
                <Button variant='primary' onClick={submit} disabled={!title.trim()}>
                    Add {type}
                </Button>
            </div>
        </Modal>
    );
}

/**
 * Returns a placeholder prompt for each node type input.
 */
function placeholder(type: 'outcome' | 'opportunity' | 'solution' | 'story' | 'experiment' | 'enablerNode'): string {
    if (type === 'outcome') return 'e.g. Help product folk ideate and consolidate insights';
    if (type === 'opportunity') return 'e.g. Miro/Figma too generic and bloated';
    if (type === 'solution') return 'e.g. Specialized OST UX with automatic relationships';
    if (type === 'story') return 'e.g. Ability to create a Node for each type in the OST';
    if (type === 'experiment') return 'e.g. Validate assumptions via user test or prototype';
    if (type === 'enablerNode') return 'e.g. Build the shared auth middleware other stories depend on';
    return '';
}
